/**
 * RequestList Component
 * Displays a list of request templates with actions
 */

import * as React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Text,
  Badge,
  Spinner,
  Tooltip,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  EyeRegular,
  EditRegular,
  DeleteRegular,
  CopyRegular,
  LinkRegular,
  MoreHorizontalRegular,
  PlayRegular,
  PauseRegular,
} from '@fluentui/react-icons';
import type { RequestTemplate, RequestStatus } from '@structured-request/core';
import { getStatusLabel } from '@structured-request/core';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

const STATUS_COLORS: Record<RequestStatus, 'success' | 'warning' | 'danger' | 'informative' | 'subtle'> = {
  draft: 'subtle',
  active: 'success',
  paused: 'warning',
  expired: 'danger',
  completed: 'informative',
};

export interface RequestListProps {
  /** List of request templates */
  requests: RequestTemplate[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when create is clicked */
  onCreate?: () => void;
  /** Callback when view is clicked */
  onView?: (request: RequestTemplate) => void;
  /** Callback when edit is clicked */
  onEdit?: (request: RequestTemplate) => void;
  /** Callback when delete is clicked */
  onDelete?: (request: RequestTemplate) => void;
  /** Callback when copy link is clicked */
  onCopyLink?: (request: RequestTemplate) => void;
  /** Callback when status toggle is clicked */
  onToggleStatus?: (request: RequestTemplate) => void;
  /** Callback when duplicate is clicked */
  onDuplicate?: (request: RequestTemplate) => void;
  /** Base URL for generating response links */
  baseUrl?: string;
  /** Show the card wrapper (default: true) */
  showCard?: boolean;
  /** Title for the card header */
  title?: string;
}

export const RequestList: React.FC<RequestListProps> = ({
  requests,
  isLoading = false,
  onCreate,
  onView,
  onEdit,
  onDelete,
  onCopyLink,
  onToggleStatus,
  onDuplicate,
  baseUrl: _baseUrl,
  showCard = true,
  title = 'Request Templates',
}) => {
  const styles = useStyles();

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString();
  };

  const content = (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.emptyState}>
          <Spinner label="Loading requests..." />
        </div>
      ) : requests.length === 0 ? (
        <div className={styles.emptyState}>
          <Text block style={{ marginBottom: tokens.spacingVerticalM }}>
            No request templates yet.
          </Text>
          {onCreate && (
            <Button appearance="primary" icon={<AddRegular />} onClick={onCreate}>
              Create Your First Request
            </Button>
          )}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Fields</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <TableCellLayout>
                    <div>
                      <Text weight="semibold">{request.title}</Text>
                      {request.description && (
                        <Text size={200} block style={{ color: tokens.colorNeutralForeground3 }}>
                          {request.description.slice(0, 50)}
                          {request.description.length > 50 ? '...' : ''}
                        </Text>
                      )}
                    </div>
                  </TableCellLayout>
                </TableCell>
                <TableCell>
                  <Badge
                    appearance="filled"
                    color={STATUS_COLORS[request.status]}
                  >
                    {getStatusLabel(request.status)}
                  </Badge>
                  {request.oneTimeUse && (
                    <Badge
                      appearance="outline"
                      color="informative"
                      style={{ marginLeft: tokens.spacingHorizontalXS }}
                    >
                      One-time
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Text>{request.fields.length}</Text>
                </TableCell>
                <TableCell>
                  <Text size={200}>{formatDate(request.createdAt)}</Text>
                </TableCell>
                <TableCell>
                  <div className={styles.actions}>
                    {onView && (
                      <Tooltip content="View details" relationship="label">
                        <Button
                          icon={<EyeRegular />}
                          appearance="subtle"
                          size="small"
                          onClick={() => onView(request)}
                        />
                      </Tooltip>
                    )}

                    {onCopyLink && request.status === 'active' && (
                      <Tooltip content="Copy response link" relationship="label">
                        <Button
                          icon={<LinkRegular />}
                          appearance="subtle"
                          size="small"
                          onClick={() => onCopyLink(request)}
                        />
                      </Tooltip>
                    )}

                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          icon={<MoreHorizontalRegular />}
                          appearance="subtle"
                          size="small"
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {onEdit && (
                            <MenuItem icon={<EditRegular />} onClick={() => onEdit(request)}>
                              Edit
                            </MenuItem>
                          )}
                          {onToggleStatus && request.status !== 'expired' && request.status !== 'completed' && (
                            <MenuItem
                              icon={request.status === 'active' ? <PauseRegular /> : <PlayRegular />}
                              onClick={() => onToggleStatus(request)}
                            >
                              {request.status === 'active' ? 'Pause' : 'Activate'}
                            </MenuItem>
                          )}
                          {onDuplicate && (
                            <MenuItem icon={<CopyRegular />} onClick={() => onDuplicate(request)}>
                              Duplicate
                            </MenuItem>
                          )}
                          {onDelete && (
                            <MenuItem icon={<DeleteRegular />} onClick={() => onDelete(request)}>
                              Delete
                            </MenuItem>
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardHeader
        header={<Text size={400} weight="semibold">{title}</Text>}
        description={<Text size={200}>{requests.length} template(s)</Text>}
        action={
          onCreate && requests.length > 0 ? (
            <Button icon={<AddRegular />} appearance="primary" onClick={onCreate}>
              New Request
            </Button>
          ) : undefined
        }
      />
      <CardPreview style={{ padding: tokens.spacingVerticalL }}>
        {content}
      </CardPreview>
    </Card>
  );
};

export default RequestList;
